import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatDate, formatApiError } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

const STATE_OPTS = ["all", "draft_intake", "nivel0_pending", "nivel0_ready", "out_of_scope",
  "nivel1_payment_pending", "nivel1_paid", "nivel1_processing", "nivel1_needs_human_review",
  "nivel1_revision_requested", "nivel1_delivered",
  "nivel2_offer_sent", "nivel2_payment_pending", "nivel2_paid", "nivel2_processing",
  "nivel2_needs_human_review", "nivel2_revision_requested", "nivel2_delivered", "closed"];
const PAY_OPTS = ["all", "unpaid", "pending_confirmation", "paid", "failed", "refunded"];
const REV_OPTS = ["all", "not_started", "in_review", "approved", "rejected"];
const LEVEL_OPTS = ["all", "nivel0", "nivel1", "nivel2"];

// Estados de destino comunes para bulk transitions
const BULK_TARGET_STATES = [
  "out_of_scope",
  "closed",
  "nivel1_processing",
  "nivel1_needs_human_review",
  "nivel1_delivered",
  "nivel2_processing",
  "nivel2_needs_human_review",
  "nivel2_delivered",
];

// Estados que requieren un motivo (≥10 chars)
const REASON_REQUIRED_STATES = new Set([
  "closed", "out_of_scope",
  "nivel1_revision_requested", "nivel2_revision_requested",
]);

export default function AdminCasesListPage() {
  const [filters, setFilters] = useState({
    state: "all", payment_status: "all", review_status: "all",
    assigned_reviewer_id: "", has_generation_error: false,
    stuck_in_processing: false, q: "",
    level: "all", created_after: "", created_before: "",
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [], pages: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [reviewers, setReviewers] = useState([]);
  const [bulkAction, setBulkAction] = useState(null); // "transition" | "assign" | null
  const [bulkTarget, setBulkTarget] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkReviewerId, setBulkReviewerId] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), limit: "50" });
      if (filters.state !== "all") p.set("state", filters.state);
      if (filters.payment_status !== "all") p.set("payment_status", filters.payment_status);
      if (filters.review_status !== "all") p.set("review_status", filters.review_status);
      if (filters.assigned_reviewer_id) p.set("assigned_reviewer_id", filters.assigned_reviewer_id);
      if (filters.has_generation_error) p.set("has_generation_error", "true");
      if (filters.stuck_in_processing) p.set("stuck_in_processing", "true");
      if (filters.level && filters.level !== "all") p.set("level", filters.level);
      if (filters.created_after) p.set("created_after", filters.created_after);
      if (filters.created_before) p.set("created_before", filters.created_before);
      if (filters.q && filters.q.trim()) p.set("q", filters.q.trim());
      const { data: d } = await api.get(`/admin/cases?${p}`);
      setData(d);
      // Limpiar selección: solo conservar IDs que sigan presentes en la página
      setSelected((prev) => {
        const visible = new Set((d.items || []).map((c) => c.id));
        const next = new Set();
        for (const id of prev) { if (visible.has(id)) next.add(id); }
        return next;
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando casos");
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  // Cargar lista de revisores (una vez)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get("/admin/reviewers");
        if (alive) setReviewers(d?.items || d || []);
      } catch {
        // silencioso
      }
    })();
    return () => { alive = false; };
  }, []);

  const exportCsv = () => {
    const rows = [["id", "title", "owner_email", "state", "payment_status", "review_status", "created_at"]];
    data.items.forEach((c) => rows.push([c.id, c.title, c.user_email, c.state, c.payment_status, c.review_status, c.created_at]));
    const csv = rows.map((r) => r.map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `casos-pagina-${page}.csv`;
    a.click();
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const allIds = data.items.map((c) => c.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allIds));
    }
  };

  const openBulkTransition = () => {
    setBulkTarget("");
    setBulkReason("");
    setBulkResult(null);
    setBulkAction("transition");
  };

  const openBulkAssign = () => {
    setBulkReviewerId("");
    setBulkResult(null);
    setBulkAction("assign");
  };

  const closeBulk = () => {
    setBulkAction(null);
    setBulkResult(null);
  };

  const runBulkTransition = async () => {
    if (!bulkTarget) {
      toast.error("Selecciona un estado de destino");
      return;
    }
    if (REASON_REQUIRED_STATES.has(bulkTarget) && bulkReason.trim().length < 10) {
      toast.error("Este estado requiere un motivo de ≥10 caracteres");
      return;
    }
    setBulkRunning(true);
    try {
      const { data: res } = await api.post("/admin/cases/bulk-transition", {
        case_ids: Array.from(selected),
        target_state: bulkTarget,
        reason: bulkReason.trim() || undefined,
      });
      setBulkResult(res);
      if (res.failed === 0) {
        toast.success(`${res.succeeded} caso(s) actualizados correctamente`);
      } else {
        toast.warning(`${res.succeeded} OK, ${res.failed} con error`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error en operación bulk");
    } finally {
      setBulkRunning(false);
    }
  };

  const runBulkAssign = async () => {
    setBulkRunning(true);
    try {
      const { data: res } = await api.post("/admin/cases/bulk-assign", {
        case_ids: Array.from(selected),
        reviewer_id: bulkReviewerId || null,
      });
      setBulkResult({
        ok: res.ok,
        total: res.total_requested,
        succeeded: res.modified_count,
        failed: res.total_requested - res.modified_count,
        reviewer_email: res.reviewer_email,
      });
      toast.success(
        bulkReviewerId
          ? `Asignados ${res.modified_count} de ${res.total_requested} a ${res.reviewer_email}`
          : `Desasignados ${res.modified_count} casos`,
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error asignando");
    } finally {
      setBulkRunning(false);
    }
  };

  const allOnPageSelected =
    data.items.length > 0 && data.items.every((c) => selected.has(c.id));
  const selectedCount = selected.size;
  const reasonRequired = REASON_REQUIRED_STATES.has(bulkTarget);

  return (
    <div data-testid="admin-cases-page">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Gestión</p>
          <h1 className="font-serif text-3xl text-gray-900">Casos</h1>
          <p className="text-xs text-gray-500 mt-1">{data.total} casos en total</p>
        </div>
        <Button variant="outline" className="rounded-sm border-gray-300" onClick={exportCsv} data-testid="cases-export">Exportar CSV</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-4 items-end">
        <FilterSelect label="Estado" v={filters.state} onChange={(v) => setFilters((f) => ({ ...f, state: v }))} opts={STATE_OPTS} testid="filter-state" />
        <FilterSelect label="Nivel" v={filters.level} onChange={(v) => setFilters((f) => ({ ...f, level: v }))} opts={LEVEL_OPTS} testid="filter-level" />
        <FilterSelect label="Pago" v={filters.payment_status} onChange={(v) => setFilters((f) => ({ ...f, payment_status: v }))} opts={PAY_OPTS} testid="filter-pay" />
        <FilterSelect label="Revisión" v={filters.review_status} onChange={(v) => setFilters((f) => ({ ...f, review_status: v }))} opts={REV_OPTS} testid="filter-rev" />
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Desde</label>
          <Input type="date" value={filters.created_after} onChange={(e) => setFilters((f) => ({ ...f, created_after: e.target.value }))} className="rounded-sm h-9" data-testid="filter-date-from" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Hasta</label>
          <Input type="date" value={filters.created_before} onChange={(e) => setFilters((f) => ({ ...f, created_before: e.target.value }))} className="rounded-sm h-9" data-testid="filter-date-to" />
        </div>
        <div className="col-span-2 sm:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Buscar</label>
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="título/objeto/id" className="rounded-sm h-9" data-testid="cases-q" />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-700 mb-1 col-span-2 sm:col-span-1">
          <input type="checkbox" checked={filters.has_generation_error} onChange={(e) => setFilters((f) => ({ ...f, has_generation_error: e.target.checked }))} data-testid="filter-gen-err" />
          Error generación
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-700 mb-1 col-span-2 sm:col-span-1">
          <input type="checkbox" checked={filters.stuck_in_processing} onChange={(e) => setFilters((f) => ({ ...f, stuck_in_processing: e.target.checked }))} data-testid="filter-stuck" />
          Atascados &gt;10m
        </label>
      </div>

      {/* Bulk actions bar */}
      {selectedCount > 0 && (
        <div
          className="mb-3 bg-brand-cream border border-brand-burgundy/30 px-3 py-2 rounded-sm flex items-center justify-between gap-3 flex-wrap"
          data-testid="bulk-actions-bar"
        >
          <p className="text-sm font-medium text-brand-burgundy">
            {selectedCount} caso{selectedCount === 1 ? "" : "s"} seleccionado{selectedCount === 1 ? "" : "s"}
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm h-8 border-brand-burgundy text-brand-burgundy hover:bg-brand-burgundy hover:text-white"
              onClick={openBulkTransition}
              data-testid="bulk-transition-button"
            >
              Cambiar estado
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm h-8 border-brand-burgundy text-brand-burgundy hover:bg-brand-burgundy hover:text-white"
              onClick={openBulkAssign}
              data-testid="bulk-assign-button"
            >
              Asignar revisor
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-sm h-8 text-gray-600"
              onClick={() => setSelected(new Set())}
              data-testid="bulk-clear"
            >
              Limpiar
            </Button>
          </div>
        </div>
      )}

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
            <th className="px-3 py-3 w-8">
              <input
                type="checkbox"
                checked={allOnPageSelected}
                onChange={toggleAll}
                aria-label="Seleccionar todos en esta página"
                data-testid="bulk-select-all"
              />
            </th>
            <th className="px-3 py-3 text-left">ID</th>
            <th className="px-3 py-3 text-left">Título</th>
            <th className="px-3 py-3 text-left">Owner</th>
            <th className="px-3 py-3 text-left">Estado</th>
            <th className="px-3 py-3 text-left">Pago</th>
            <th className="px-3 py-3 text-left">Revisión</th>
            <th className="px-3 py-3 text-left">Revisor</th>
            <th className="px-3 py-3 text-right">Creado</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Cargando…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500" data-testid="cases-empty">Sin resultados.</td></tr>}
            {!loading && data.items.map((c) => (
              <tr
                key={c.id}
                className={`border-b border-gray-100 hover:bg-gray-50 ${selected.has(c.id) ? "bg-brand-cream/40" : ""}`}
                data-testid={`case-row-${c.id}`}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleOne(c.id)}
                    aria-label={`Seleccionar ${c.title}`}
                    data-testid={`bulk-select-${c.id}`}
                  />
                </td>
                <td className="px-3 py-3 font-mono text-[11px] text-gray-500">{c.id?.slice(0, 8)}</td>
                <td className="px-3 py-3 max-w-[280px]"><Link to={`/admin/cases/${c.id}`} className="text-gray-900 underline-offset-2 hover:underline truncate inline-block max-w-full">{c.title}</Link></td>
                <td className="px-3 py-3 text-gray-700 text-xs truncate max-w-[180px]">{c.user_email || "—"}</td>
                <td className="px-3 py-3 text-xs">{c.state}</td>
                <td className="px-3 py-3 text-xs">{c.payment_status || "—"}</td>
                <td className="px-3 py-3 text-xs">{c.review_status || "—"}</td>
                <td className="px-3 py-3 text-xs truncate max-w-[160px]">{c.assigned_reviewer_email || "—"}</td>
                <td className="px-3 py-3 text-xs text-gray-500 text-right whitespace-nowrap">{formatDate(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <div className="flex justify-between items-center mt-3 text-xs text-gray-600">
          <span>Página {page} de {data.pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="cases-prev">Anterior</Button>
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="cases-next">Siguiente</Button>
          </div>
        </div>
      )}

      {/* Bulk transition modal */}
      <Dialog open={bulkAction === "transition"} onOpenChange={(o) => { if (!o) closeBulk(); }}>
        <DialogContent className="max-w-md" data-testid="bulk-transition-modal">
          <DialogHeader>
            <DialogTitle>Cambiar estado en bloque</DialogTitle>
            <DialogDescription>
              Aplica la misma transición a {selectedCount} caso{selectedCount === 1 ? "" : "s"}.
              Los casos cuyo estado actual no permita esta transición se reportarán como error.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult && (
            <div className="space-y-3 mt-2">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Estado destino</label>
                <Select value={bulkTarget} onValueChange={setBulkTarget}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="bulk-target-select">
                    <SelectValue placeholder="Elegir estado…" />
                  </SelectTrigger>
                  <SelectContent>
                    {BULK_TARGET_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {reasonRequired && (
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
                    Motivo (≥10 caracteres) <span className="text-rose-600">*</span>
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-sm p-2 text-sm h-20"
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                    data-testid="bulk-reason-input"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">{bulkReason.trim().length} caracteres</p>
                </div>
              )}
            </div>
          )}

          {bulkResult && (
            <div className="space-y-2 mt-2 max-h-60 overflow-y-auto" data-testid="bulk-result-summary">
              <p className="text-sm">
                <span className="text-emerald-700 font-semibold">{bulkResult.succeeded} éxito{bulkResult.succeeded === 1 ? "" : "s"}</span>
                {bulkResult.failed > 0 && (
                  <span className="text-rose-700 ml-2">· {bulkResult.failed} error{bulkResult.failed === 1 ? "" : "es"}</span>
                )}
              </p>
              {(bulkResult.items || []).filter((i) => i.status === "error").map((i) => (
                <div key={i.case_id} className="bg-rose-50 border-l-2 border-rose-500 p-2 text-xs">
                  <p className="font-mono text-rose-900">{i.case_id?.slice(0, 8)} · {i.code}</p>
                  <p className="text-rose-700">{i.error}</p>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="mt-4">
            {!bulkResult && (
              <>
                <Button variant="outline" onClick={closeBulk} data-testid="bulk-transition-cancel">Cancelar</Button>
                <Button
                  className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
                  disabled={bulkRunning || !bulkTarget || (reasonRequired && bulkReason.trim().length < 10)}
                  onClick={runBulkTransition}
                  data-testid="bulk-transition-confirm"
                >
                  {bulkRunning ? "Aplicando…" : `Aplicar a ${selectedCount}`}
                </Button>
              </>
            )}
            {bulkResult && (
              <Button variant="outline" onClick={closeBulk} data-testid="bulk-result-close">Cerrar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk assign modal */}
      <Dialog open={bulkAction === "assign"} onOpenChange={(o) => { if (!o) closeBulk(); }}>
        <DialogContent className="max-w-md" data-testid="bulk-assign-modal">
          <DialogHeader>
            <DialogTitle>Asignar revisor en bloque</DialogTitle>
            <DialogDescription>
              Asigna un mismo revisor a {selectedCount} caso{selectedCount === 1 ? "" : "s"}, o desasigna a todos.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult && (
            <div className="space-y-3 mt-2">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Revisor</label>
                <Select value={bulkReviewerId || "_unassign"} onValueChange={(v) => setBulkReviewerId(v === "_unassign" ? "" : v)}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="bulk-reviewer-select">
                    <SelectValue placeholder="Elegir revisor…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_unassign">— Desasignar (sin revisor) —</SelectItem>
                    {reviewers.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.email || r.name || r.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {bulkResult && (
            <div className="mt-2" data-testid="bulk-assign-result">
              <p className="text-sm">
                <span className="text-emerald-700 font-semibold">
                  {bulkResult.succeeded} de {bulkResult.total} caso(s) actualizados
                </span>
                {bulkResult.reviewer_email && <span className="text-gray-700"> · {bulkResult.reviewer_email}</span>}
              </p>
            </div>
          )}

          <DialogFooter className="mt-4">
            {!bulkResult && (
              <>
                <Button variant="outline" onClick={closeBulk} data-testid="bulk-assign-cancel">Cancelar</Button>
                <Button
                  className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
                  disabled={bulkRunning}
                  onClick={runBulkAssign}
                  data-testid="bulk-assign-confirm"
                >
                  {bulkRunning ? "Aplicando…" : (bulkReviewerId ? `Asignar a ${selectedCount}` : `Desasignar ${selectedCount}`)}
                </Button>
              </>
            )}
            {bulkResult && (
              <Button variant="outline" onClick={closeBulk} data-testid="bulk-assign-result-close">Cerrar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ label, v, onChange, opts, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <Select value={v} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm h-9 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o === "all" ? "Todos" : o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
